import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector } from '../../../Hooks/Hooks';
import { RootState } from '../../../Redux/Store';
import { AppContext } from '../../../Context/AppContext';
import { fbLogEvent } from '../../../Helpers/FirebaseHelper';
import { EVENT, LEAD, SCREEN } from '../../../Constants/AnalyticsConstants';
import { TOOLTIP_MESSAGE, LOGOUT_TEXT, CANCEL_TEXT } from '../../../Constants/Constants';
import { isEntitled } from '../../../Utils/ContentOrConfigUtil';
import confirm from '../../../../assets/icon/confirm.svg';
import ToolTip from '../../Shared/Tooltip/Tooltip';
import CustomModal, { ETitleWithOption } from '../../Shared/CustomModal/CustomModal';
import './ProfileDropdownMenu.scss';

interface IProfileDropdownMenu {
  isMenuVisible: boolean;
  handleMenuClose: () => void;
}

interface IMenuItems {
  icon: string;
  title: string;
  redirectUrl: string;
  isLogout?: boolean;
  isResetPassword?: boolean;
}
const dropdownItems = [
  {
    icon: '/oo/assets/icon/Manage_Users.svg',
    title: 'Manage Users',
    redirectUrl: '',
  },
  {
    icon: '/oo/assets/icon/Preferences_Menu.svg',
    title: 'Preferences',
    redirectUrl: '/preferences',
    slug: 'preferences',
  },
  {
    icon: '/oo/assets/icon/change-password.svg',
    title: 'Reset Password',
    redirectUrl: '',
    isResetPassword: true,
  },
  {
    icon: '/oo/assets/icon/Logout-Icon.svg',
    title: LOGOUT_TEXT,
    redirectUrl: '',
    isLogout: true,
  },
];

const ProfileDropdownMenu = (props: IProfileDropdownMenu) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLInputElement>(null);
  const { AppLogout, setPreferencesClicked } = useContext(AppContext);
  const { isMenuVisible, handleMenuClose }: IProfileDropdownMenu = props;
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const [activeTooltip, setActiveTooltip] = useState<string>('');
  const [logoutModalProps, setLogoutModalProps] = useState({
    isModalOpen: false,
  });
  const [resetPasswordModalProps, setResetPasswordModalProps] = useState({
    isModalOpen: false,
  });

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });

  const handleOutsideClick = (e: any) => {
    if (
      !e?.target?.className?.includes('Menu_Dropdown_Arrow') &&
      menuRef.current &&
      !menuRef.current.contains(e.target)
    ) {
      isMenuVisible && handleMenuClose();
      setActiveTooltip('');
    }
  };

  const handleMenuClick = (item: IMenuItems): void => {
    if (item?.redirectUrl === '/preferences') {
      setPreferencesClicked(true);
      fbLogEvent(EVENT.UE_PREFERENCES, {
        screen: SCREEN.LEFT_NAV_ITEM,
        lead: SCREEN.TOP_MENU_WEB,
      });
    }
    if (item.isLogout) {
      fbLogEvent(EVENT.UE_LOGOUT, {
        screen: SCREEN.TOP_MENU_WEB,
        lead: SCREEN.TOP_MENU_WEB,
      });
      handleMenuClose();
      handleLogoutButtonClick();
      return;
    } else if (item?.redirectUrl) {
      isMenuVisible && handleMenuClose();
      return navigate(item?.redirectUrl);
    }
    // if (item.isResetPassword) {
    //   handleMenuClose();
    //   handleResetPasswordButtonClick();
    //   return;
    // }
    return setActiveTooltip(item?.title);
  };

  const handleLogoutButtonClick = () => {
    const temp = {
      isModalOpen: true,
      title: ETitleWithOption.Confirmation,
      titleIcon: confirm,
      additionalCssClass: 'Modal_Logout',
      backdropDismiss: false,
      message: "<div class='my-3'><h3>Are you sure you want to log out?</h3></div>",
      size: 'small-height',
      buttons: [
        {
          title: LOGOUT_TEXT,
          type: 'secondary-cta-btn',
          onClick: () => {
            fbLogEvent(EVENT.UE_LOGOUT_CONFIRM, {
              screen: SCREEN.LOGOUT_CONFIRMATION,
              lead: SCREEN.TOP_MENU_WEB,
            });
            AppLogout();
          },
        },
        {
          title: CANCEL_TEXT,
          type: 'primary-cta-btn',
          onClick: () => {
            fbLogEvent(EVENT.UE_LOGOUT_CANCEL, {
              screen: SCREEN.LOGOUT_CONFIRMATION,
              lead: SCREEN.TOP_MENU_WEB,
            });
            setLogoutModalProps({ isModalOpen: false });
          },
        },
      ],
    };
    fbLogEvent(EVENT.SV_LOGOUT_CONFIRMATION, {
      screen: SCREEN.LOGOUT_CONFIRMATION,
      lead: SCREEN.TOP_MENU_WEB,
    });
    setLogoutModalProps(temp);
  };

  return (
    <div>
      <CustomModal {...logoutModalProps} />
      <CustomModal {...resetPasswordModalProps} />

      {isMenuVisible && (
        <div className="Dropdown_Container pa-3" ref={menuRef}>
          <div className="Dropdown_Menu">
            <div className="ProfileDropdown_Container">
              <div className="Profile_Details_Container">
                <div>
                  <img className="User_Icon" src="/oo/assets/icon/man.svg" alt="User Icon" />
                </div>
                <div className="Details_Column">
                  <p className="User_Name_Row">
                    {memberData?.member?.firstName || 'User'} {memberData?.member?.lastName}
                  </p>
                  <p className="User_Email_Row">{memberData?.member?.upn}</p>
                </div>
              </div>
              <button
                className="primary-cta-btn mx-5"
                onClick={() => {
                  handleMenuClose();
                  fbLogEvent(EVENT.UE_MY_PROFILE, {
                    screen: SCREEN.MY_PROFILE,
                    lead: LEAD.TOP_MENU_WEB,
                  });
                  navigate('/profile');
                }}
              >
                My Profile
              </button>
            </div>
            <div className="pa-3 Menu_Container">
              {dropdownItems?.map((item: any) => {
                const isPreferences = item?.slug === 'preferences';
                const isNotEntitled = !isEntitled(
                  memberData?.member,
                  configTableData?.FPO_APP_CONFIG?.APP_INIT,
                  'tab',
                  item?.slug,
                );
                if (!(isPreferences && isNotEntitled)) {
                  return (
                    <div key={item.title}>
                      <div className="Tooltip_Container">
                        <ToolTip
                          message={TOOLTIP_MESSAGE}
                          isActive={item?.title === activeTooltip}
                          additionalClass={'tooltip-menu'}
                          clearIndex={true}
                        />
                      </div>
                      <div
                        key={item.title}
                        onClick={() => {
                          handleMenuClick(item);
                        }}
                        className="Item_Container"
                      >
                        <img src={item.icon} alt={item.title} />
                        <p>{item.title}</p>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdownMenu;
